import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  makeStyles,
  Theme,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import {
  size,
} from "@mvp/design-system";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useBool } from "../../../hooks/useBool";
import routes from "../../../routes";
import { MobileDetails } from "./Details";
import { MobileSummary } from "./Summary";

const useStyles = makeStyles<Theme, {adjustForThankYou: boolean; headerHeight: number}>((theme) => ({ 
  root: {
    width: "100%",
    position: "fixed",
    bottom: ({adjustForThankYou}) => adjustForThankYou ? "0" : "4rem",
    left: 0,
    right: 0,
    zIndex: 1000,
    overflowY: "scroll",
    overflowX: "hidden",
    // stop pull-down at the top of the panel from bouncing/scrolling the page behind it
    overscrollBehavior: "contain",
    borderTop: ".125rem solid #DDDAD7",
    borderBottom: ".125rem solid #DDDAD7",
    "&.Mui-expanded": {
      marginBottom: 0,
      zIndex: 1001,
      // stay above the fixed Back/Continue button bar (NavigationButtons, height 4rem) so it isn't covered
      bottom: ({adjustForThankYou}) => adjustForThankYou ? "0" : "4rem",
      // pin below the header's real height so it's never obscured, content scrolls internally
      top: ({headerHeight}) => `${headerHeight}px`,
      maxHeight: "none",
      borderTop: "hidden",
    },
  },
  accordionDetails: {
    paddingLeft: size.s(theme),
    paddingRight: size.s(theme),
  },
  iconStyles: {
    transform: "rotate(180deg)",
  },
  accordionSummary: {
    "&.Mui-expanded": {
      alignItems: "start",
    },
  },
}));

export const MobilePlanOverview = () => {
  const location = useLocation();

  const locationPath = location.pathname;
  const isThankYouPage = locationPath === routes.reviewSelection.thankYou;

  const [headerHeight, setHeaderHeight] = useState(0);
  const classes = useStyles({adjustForThankYou: isThankYouPage, headerHeight});
  const { bool: isOpen, flipBool } = useBool(false);

  // measure the real header height so the expanded panel never overlaps it
  useEffect(() => {
    const headerEl = document.querySelector<HTMLElement>(".MuiAppBar-root");
    if (!headerEl) return;

    const updateHeaderHeight = () => setHeaderHeight(headerEl.getBoundingClientRect().bottom);
    updateHeaderHeight();

    window.addEventListener("resize", updateHeaderHeight);

    return () => {
      window.removeEventListener("resize", updateHeaderHeight);
    };
  }, []);

  // lock body scroll while open so iOS Safari can't rubber-band the page and shift this fixed panel
  useEffect(() => {
    if (!isOpen) return;

    const scrollY = window.scrollY;
    const { body } = document;
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";

    return () => {
      body.style.position = "";
      body.style.top = "";
      body.style.width = "";
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  return (
    <Accordion className={classes.root}>
      <AccordionSummary
        onClick={flipBool}
        className={classes.accordionSummary}
        expandIcon={
          isOpen ? (
            <CloseIcon
              color="primary"
              fontSize="large"
              style={{ marginBottom: ".5rem" }}
            />
          ) : (
            <ExpandMoreIcon
              color="primary"
              fontSize="large"
              className={classes.iconStyles}
            />
          )
        }
      >
        <MobileSummary isOpen={isOpen} />
      </AccordionSummary>
      <AccordionDetails className={classes.accordionDetails}>
        <MobileDetails />
      </AccordionDetails>
    </Accordion>
  );
};
